import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import moment from 'moment-timezone';
import toast from 'react-hot-toast';
import { APP_TIMEZONE, parseAnalyticsTimestamp, formatAllLocationsHoursLabel } from '../utils/timezone';
import { buildDashboardCacheKey, getOperationalDateRange } from '../utils/dashboardAnalytics';
import { fetchDashboardAnalytics } from '../Store/actions/dashboard';
import {
  selectDashboardAnalysisData,
  selectDashboardDayWiseData,
  selectDashboardDayWiseLoading,
  selectDashboardError,
  selectDashboardLoading,
  selectDashboardActiveCacheKey,
} from '../Store/selectors/dashboard';
import DateRangePicker from '../components/ui/date-range-picker';
import ResetTokesButton from '../components/reset-tokes-button';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { dashboardTournamentTagService } from '../apis/dashboardTournamentTagService';
import DayWiseBreakdown from '../components/dashboard/DayWiseBreakdown';
import { DashboardHeader, DashboardLoader, DashboardStatCard } from '../components/dashboard/DashboardShell';
import { useDashboardChat } from '../context/DashboardChatContext';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  Title, Tooltip, Legend
);

const MCALLEN_LABEL = 'McAllen EPL';
const BROWNSVILLE_LABEL = 'Brownsville EPL';
const EMPTY_SOURCE = { labels: [], tables: [], game_name: [] };

const Dashboard = () => {
  const dispatch = useDispatch();
  const analysisData = useSelector(selectDashboardAnalysisData);
  const dayWiseData = useSelector(selectDashboardDayWiseData);
  const analysisLoading = useSelector(selectDashboardLoading);
  const analysisError = useSelector(selectDashboardError);
  const dayWiseLoading = useSelector(selectDashboardDayWiseLoading);
  const activeCacheKey = useSelector(selectDashboardActiveCacheKey);

  const [chartType, setChartType] = useState('bar');
  const [dateRange, setDateRange] = useState({
    start: moment().tz(APP_TIMEZONE).startOf('month').toDate(),
    end: moment().tz(APP_TIMEZONE).endOf('month').toDate(),
    label: 'This Month'
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [eplChartView, setEplChartView] = useState('time');
  const [tournamentOverrides, setTournamentOverrides] = useState({});
  const [togglingTournamentGame, setTogglingTournamentGame] = useState(null);
  const [tagPickerGame, setTagPickerGame] = useState(null);
  const [showDayWiseSection, setShowDayWiseSection] = useState(false);
  const dayWiseRecoveryKeyRef = useRef(null);
  const { syncDateRange } = useDashboardChat();

  useEffect(() => {
    syncDateRange(dateRange.start, dateRange.end);
  }, [dateRange.end, dateRange.start, syncDateRange]);

  const MCALLEN_COLOR = 'rgba(54, 162, 235, 1)';
  const BROWNSVILLE_COLOR = 'rgba(245, 158, 11, 1)';
  const MCALLEN_LIGHT = 'rgba(54, 162, 235, 0.2)';
  const BROWNSVILLE_LIGHT = 'rgba(245, 158, 11, 0.2)';

  const isBrownsvilleLive = (data) =>
    !!(data && data.brownsville && data.brownsville.labels && data.brownsville.labels.length > 0);

  const isMultipleDaysRange = useCallback(() => {
    const start = moment(dateRange.start).tz(APP_TIMEZONE);
    const end = moment(dateRange.end).tz(APP_TIMEZONE);
    return !start.isSame(end, 'day');
  }, [dateRange.start, dateRange.end]);

  const loadDashboardAnalytics = useCallback(
    (forceRefresh = false) => {
      const { startDate, endDate, isSingleDay } = getOperationalDateRange(
        dateRange.start,
        dateRange.end
      );
      const cacheKey = buildDashboardCacheKey(startDate, endDate);
      const hasDayWise = dayWiseData && Object.keys(dayWiseData).length > 0;

      if (
        !forceRefresh &&
        activeCacheKey === cacheKey &&
        analysisData &&
        (isSingleDay || hasDayWise)
      ) {
        return;
      }

      startTransition(() => {
        dispatch(
          fetchDashboardAnalytics({
            startDate,
            endDate,
            isSingleDay,
            rangeStart: dateRange.start,
            rangeEnd: dateRange.end,
            isMultiDay: !isSingleDay,
            forceRefresh,
          })
        );
      });
    },
    [activeCacheKey, analysisData, dateRange.end, dateRange.start, dayWiseData, dispatch]
  );

  useEffect(() => {
    loadDashboardAnalytics(false);
  }, [loadDashboardAnalytics]);

  // Recover incomplete multi-day cache (main chart present, day-wise missing)
  useEffect(() => {
    if (!isMultipleDaysRange()) return;
    if (analysisLoading || dayWiseLoading) return;
    if (!analysisData) return;
    if (Object.keys(dayWiseData).length > 0) {
      dayWiseRecoveryKeyRef.current = null;
      return;
    }

    const { startDate, endDate } = getOperationalDateRange(dateRange.start, dateRange.end);
    const recoveryKey = `${startDate}_${endDate}`;
    if (dayWiseRecoveryKeyRef.current === recoveryKey) return;
    dayWiseRecoveryKeyRef.current = recoveryKey;
    loadDashboardAnalytics(true);
  }, [
    dateRange.start,
    dateRange.end,
    dayWiseData,
    dayWiseLoading,
    analysisData,
    analysisLoading,
    isMultipleDaysRange,
    loadDashboardAnalytics,
  ]);

  useEffect(() => {
    if (!isMultipleDaysRange()) {
      setShowDayWiseSection(false);
      return undefined;
    }
    if (Object.keys(dayWiseData).length === 0 && dayWiseLoading) {
      setShowDayWiseSection(true);
      return undefined;
    }
    let idleId;
    const enable = () => setShowDayWiseSection(true);
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      idleId = window.requestIdleCallback(enable, { timeout: 400 });
      return () => window.cancelIdleCallback(idleId);
    }
    idleId = window.setTimeout(enable, 120);
    return () => window.clearTimeout(idleId);
  }, [dayWiseData, dayWiseLoading, dateRange.start, dateRange.end, isMultipleDaysRange]);

  const sortedDayWiseEntries = useMemo(() => {
    const hasPoints = (entry) => {
      const mcallen = entry?.analysisData?.mcallen?.labels?.length || 0;
      const brownsville = entry?.analysisData?.brownsville?.labels?.length || 0;
      return mcallen + brownsville > 0;
    };

    return Object.values(dayWiseData)
      .filter(hasPoints)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [dayWiseData]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await dashboardTournamentTagService.getOverrides();
      if (!cancelled && result.success) {
        setTournamentOverrides(result.overrides || {});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDateRangeApply = (startDate, endDate, label) => {
    const chicagoStart = moment(startDate).tz(APP_TIMEZONE);
    const chicagoEnd = moment(endDate).tz(APP_TIMEZONE);

    setDateRange({
      start: chicagoStart.toDate(),
      end: chicagoEnd.toDate(),
      label
    });
  };

  const formatDateRangeDisplay = () => {
    const start = moment(dateRange.start).tz(APP_TIMEZONE);
    const end = moment(dateRange.end).tz(APP_TIMEZONE);

    if (start.isSame(end, 'day')) {
      return start.format('MMM DD, YYYY');
    }

    if (dateRange.label === 'Custom Range') {
      return `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`;
    }
    return dateRange.label;
  };


  // Enhanced chart options for better visualization
  const getEnhancedChartOptions = (title, chartViewType = 'time', peakInfo = null) => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      transitions: { active: { animation: { duration: 0 } } },
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        title: {
          display: true,
          text: title,
          font: { size: 16, weight: 'bold' },
          padding: 20,
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15,
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const count = context.parsed.y;
              const averages = context.dataset.averages;
              const avg = averages?.[context.dataIndex];

              if (chartViewType === 'time' && avg != null) {
                return `${context.dataset.label}: ${count} tables, Avg: ${avg} tables`;
              }

              return `${context.dataset.label}: ${count} tables`;
            },
            afterBody: function () {
              if (chartViewType !== 'time' || !peakInfo) return [];

              const lines = [];
              if (peakInfo.peakDay) {
                lines.push(`Peak day: ${peakInfo.peakDay}`);
              }
              if (peakInfo.peakHour) {
                lines.push(`Peak hour: ${peakInfo.peakHour}`);
              }
              return lines;
            }
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: 'white',
          bodyColor: 'white',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          borderWidth: 1,
        }
      },
      scales: {
        x: {
          ticks: {
            font: { size: 10 },
            maxRotation: chartViewType === 'games' ? 45 : 0,
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)',
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: { size: 12 },
            stepSize: 1,
            precision: 0,
            callback: (value) => (Number.isInteger(value) ? value : undefined),
          },
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)',
          },
          title: {
            display: true,
            text: 'Number of Tables',
            font: { size: 12, weight: 'bold' },
          }
        }
      }
    };

    return baseOptions;
  };

  // Helper function to normalize Unicode bold characters to regular characters
  // Handles Mathematical Bold Unicode (U+1D400–U+1D7FF) and other bold variants
  const normalizeUnicodeBold = (str) => {
    if (!str) return str;
    // Replace Unicode Mathematical Bold characters with regular ASCII
    return str
      .replace(/[\u{1D400}-\u{1D433}]/gu, (char) => {
        // Mathematical Bold Uppercase A-Z (U+1D400–U+1D433)
        const code = char.codePointAt(0);
        return String.fromCharCode(code - 0x1D400 + 65);
      })
      .replace(/[\u{1D434}-\u{1D45D}]/gu, (char) => {
        // Mathematical Bold Lowercase a-z (U+1D434–U+1D45D)
        const code = char.codePointAt(0);
        return String.fromCharCode(code - 0x1D434 + 97);
      })
      .replace(/[\u{1D5D4}-\u{1D607}]/gu, (char) => {
        // Mathematical Sans-Serif Bold Uppercase A-Z
        const code = char.codePointAt(0);
        return String.fromCharCode(code - 0x1D5D4 + 65);
      })
      .replace(/[\u{1D608}-\u{1D631}]/gu, (char) => {
        // Mathematical Sans-Serif Bold Lowercase a-z
        const code = char.codePointAt(0);
        return String.fromCharCode(code - 0x1D608 + 97);
      });
  };

  // Helper function to normalize Unicode digits (Mathematical Bold, etc.) to regular digits
  const normalizeUnicodeDigits = (str) => {
    if (!str) return str;

    // Mathematical Bold Digits (U+1D7CE–U+1D7D7) for 0-9
    let normalized = str.replace(/[\u{1D7CE}-\u{1D7D7}]/gu, (char) => {
      const code = char.codePointAt(0);
      return String.fromCharCode(code - 0x1D7CE + 48); // '0' is 48
    });

    // Full-width digits (U+FF10–U+FF19)
    normalized = normalized.replace(/[\uFF10-\uFF19]/g, (char) => {
      return String.fromCharCode(char.charCodeAt(0) - 0xFF10 + 48);
    });

    // Mathematical Double-Struck Digits (U+1D7D8–U+1D7E1) for 0-9
    normalized = normalized.replace(/[\u{1D7D8}-\u{1D7E1}]/gu, (char) => {
      const code = char.codePointAt(0);
      return String.fromCharCode(code - 0x1D7D8 + 48);
    });

    // Mathematical Sans-Serif Digits (U+1D7E2–U+1D7EB) for 0-9
    normalized = normalized.replace(/[\u{1D7E2}-\u{1D7EB}]/gu, (char) => {
      const code = char.codePointAt(0);
      return String.fromCharCode(code - 0x1D7E2 + 48);
    });

    // Mathematical Sans-Serif Bold Digits (U+1D7EC–U+1D7F5) for 0-9
    normalized = normalized.replace(/[\u{1D7EC}-\u{1D7F5}]/gu, (char) => {
      const code = char.codePointAt(0);
      return String.fromCharCode(code - 0x1D7EC + 48);
    });

    // Mathematical Monospace Digits (U+1D7F6–U+1D7FF) for 0-9
    normalized = normalized.replace(/[\u{1D7F6}-\u{1D7FF}]/gu, (char) => {
      const code = char.codePointAt(0);
      return String.fromCharCode(code - 0x1D7F6 + 48);
    });

    return normalized;
  };

  // Function to identify if a game is a tournament
  // STRICTLY matches ONLY these exact tournament names:
  // 1. Tuesday Turbo
  // 2. Friday Freeroll
  // 3. Sunday Funday NL Omaha
  // 4. 15K GTD (11/20 - 11/23)
  // 5. Winner Take All
  // 6. Friday Tournament (including Unicode bold: 𝐅𝐑𝐈𝐃𝐀𝐘 𝐓𝐎𝐔𝐑𝐍𝐀𝐌𝐄𝐍𝐓)
  // 7. Saturday Tournament (including Unicode bold: 𝐒𝐀𝐓𝐔𝐑𝐃𝐀𝐘 𝐓𝐎𝐔𝐑𝐍𝐀𝐌𝐄𝐍𝐓)
  // 8. Sunday Tournament (including Unicode bold: 𝐒𝐔𝐍𝐃𝐀𝐘 𝐓𝐎𝐔𝐑𝐍𝐀𝐌𝐄𝐍𝐓)
  // 9. Tuesday Tournament
  // 10. $10K in a Day IV (including Unicode bold digits and emojis)
  // 11.🏆Texas Card House Trailblazer II🏆
  // 12.EPL Monthly Classic 10K GTD
  // 13.BILLIARDS
  // 14.🏆MARCH DEEPSTACK🏆
  // 15.25K GTD $60 BUY-IN
  // 16.EPL MONTHLY CLASSIC 15K GTD
  // 17.🏆WSOP-C TCH Main Event Satellite🏆
  // 18.SATURDAY 3K GTD TURBO
  // 19.NLH 2K GREEN CHIP BOUNTY 6PM
  // 20.NLH 3K GREEN CHIP BOUNTY 6PM
  // POOL TABLE: billiards / not poker — use isExcludedFromDashboardStats, not tournaments
  // 22.ELITE 25K GTD
  // 23.ELITE 15K GTD
  // 24.SATURDAY 2K GTD TURBO
  // 25.Friday Frenzy 2K GTD: 1K GTD to 1st

  const isExcludedFromDashboardStats = (gameName) => {
    if (!gameName) return false;
    return gameName.toUpperCase().includes('POOL TABLE');
  };

  const matchesHardcodedTournament = (gameName) => {
    if (!gameName) return false;

    // First normalize Unicode digits, then Unicode bold characters, then convert to uppercase
    const normalizedDigits = normalizeUnicodeDigits(gameName);
    const normalized = normalizeUnicodeBold(normalizedDigits);
    const gameUpper = normalized.toUpperCase().trim();

    // STRICT matching - only these tournaments

    // 1. Tuesday Turbo
    if (gameUpper.includes('TUESDAY') && gameUpper.includes('TURBO')) {
      return true;
    }

    // 2. Friday Freeroll
    if (gameUpper.includes('FRIDAY') && gameUpper.includes('FREEROLL')) {
      return true;
    }

    // 3. Sunday Funday NL Omaha
    if (gameUpper.includes('SUNDAY') && gameUpper.includes('FUNDAY') && gameUpper.includes('NL OMAHA')) {
      return true;
    }

    // 4. 15K GTD (11/20 - 11/23)
    if (gameUpper.includes('15K GTD') && gameUpper.includes('11/20') && gameUpper.includes('11/23')) {
      return true;
    }

    // 5. Winner Take All
    if (gameUpper.includes('WINNER TAKE ALL')) {
      return true;
    }

    // 6. FRIDAY TOURNAMENT (handles Unicode bold characters and variations)
    if (gameUpper.includes('FRIDAY') && gameUpper.includes('TOURNAMENT')) {
      return true;
    }

    // 7. SATURDAY TOURNAMENT (handles Unicode bold characters and variations)
    if (gameUpper.includes('SATURDAY') && gameUpper.includes('TOURNAMENT')) {
      return true;
    }

    // 8. SUNDAY TOURNAMENT (handles Unicode bold characters and variations)
    if (gameUpper.includes('SUNDAY') && gameUpper.includes('TOURNAMENT')) {
      return true;
    }

    // 9. TUESDAY TOURNAMENT (handles Unicode bold characters and variations)
    if (gameUpper.includes('TUESDAY') && gameUpper.includes('TOURNAMENT')) {
      return true;
    }

    // 10.🏆Texas Card House Trailblazer II🏆
    if (gameUpper.includes('TEXAS CARD HOUSE TRAILBLAZER II')) {
      return true;
    }

    // 11. EPL Monthly Classic 10K GTD
    if (gameUpper.includes('EPL MONTHLY CLASSIC 10K GTD')) {
      return true;
    }

    // 12. 2 Year Anniversary 15K GTD (should also be treated as a tournament game and NEGLECTED from totals)
    // Neglect both "2 YEAR ANNIVERSARY 15K GTD" and variants such as "2ND YEAR ANNIVERSARY 15K GTD"
    // eslint-disable-next-line no-useless-escape
    const normalizedForAnniv = gameUpper.replace(/[\s-]+/g, ' '); // collapse spaces/hyphens, helps catch weird variants
    if (
      (
        (normalizedForAnniv.includes('2 YEAR ANNIVERSARY 15K GTD') ||
         normalizedForAnniv.includes('2ND YEAR ANNIVERSARY 15K GTD') ||
         normalizedForAnniv.includes('2-YEAR ANNIVERSARY 15K GTD'))
        ||
        // Also allow some flexible matching for this format
        (
          (normalizedForAnniv.includes('2 YEAR ANNIVERSARY') || normalizedForAnniv.includes('2ND YEAR ANNIVERSARY') || normalizedForAnniv.includes('2-YEAR ANNIVERSARY')) &&
          normalizedForAnniv.includes('15K') && normalizedForAnniv.includes('GTD')
        )
      )
    ) {
      return true;
    }

    // 13.BILLIARDS
    if (gameUpper.includes('BILLIARDS')) {
      return true;
    }

    // 15.EPL MONTHLY CLASSIC 15K GTD
    if (gameUpper.includes('EPL MONTHLY CLASSIC 15K GTD')) {
      return true;
    }

    // 16.🏆MARCH DEEPSTACK🏆
    if (gameUpper.includes('MARCH DEEPSTACK')) {
      return true;
    }
    // 17.25K GTD $60 BUY-IN
    if (gameUpper.includes('25K GTD $60 BUY-IN')) {
      return true;
    }

    // 18. WSOP-C TCH Main Event Satellite (trophy emojis optional; must use UPPER — gameUpper is .toUpperCase())
    if (gameUpper.includes('WSOP-C TCH MAIN EVENT SATELLITE')) {
      return true;
    }

    // 19. SATURDAY 3K GTD TURBO
    if (gameUpper.includes('SATURDAY 3K GTD TURBO')) {
      return true;
    }

    // 20. NLH 2K GREEN CHIP BOUNTY 6PM
    if (gameUpper.includes('NLH 2K GREEN CHIP BOUNTY 6PM')) {
      return true;
    }

    // 21. NLH 3K GREEN CHIP BOUNTY 6PM
    if (gameUpper.includes('NLH 3K GREEN CHIP BOUNTY 6PM')) {
      return true;
    }

    // 22. ELITE 25K GTD
    if (gameUpper.includes('ELITE 25K GTD')) {
      return true;
    }

    // 23. ELITE 15K GTD
    if (gameUpper.includes('ELITE 15K GTD')) {
      return true;
    }

    // 24. SATURDAY 2K GTD TURBO
    if (gameUpper.includes('SATURDAY 2K GTD TURBO')) {
      return true;
    }

    // 25. Friday Frenzy 2K GTD: 1K GTD to 1st
    // Match full name and shorter variants (gameUpper is already uppercase)
    if (
      gameUpper.includes('FRIDAY FRENZY 2K GTD: 1K GTD TO 1ST') ||
      gameUpper.includes('FRIDAY FRENZY 2K GTD') ||
      (gameUpper.includes('FRIDAY FRENZY') && gameUpper.includes('2K GTD'))
    ) {
      return true;
    }

    // $10K in a Day IV (handles Unicode bold, Unicode digits, emoji, etc)
    // Matches: "$10K in a Day IV", "🔥💸 $𝟏𝟎𝐊 𝐢𝐧 𝐚 𝐃𝐚𝐲 𝐈𝐕 💸🔥", etc.
    const originalUpper = gameName.toUpperCase();
    const has10KPattern = originalUpper.includes('10K') || originalUpper.includes('𝟏𝟎') ||
                         (originalUpper.includes('10') && originalUpper.includes('K'));
    const hasDayPattern = originalUpper.includes('DAY') || originalUpper.includes('𝐃𝐚𝐲');
    const hasIVPattern = originalUpper.includes(' IV') || originalUpper.includes('𝐈𝐕') ||
                        originalUpper.endsWith('IV') || originalUpper.includes(' IV ');

    if (has10KPattern && hasDayPattern && hasIVPattern) {
      // Additional verification: should have $ or be clearly "$10K in a Day IV"
      // Check that 10K appears before DAY, and DAY appears before IV
      const tenKPos = originalUpper.indexOf('10K') !== -1 ? originalUpper.indexOf('10K') :
                     (originalUpper.indexOf('𝟏𝟎') !== -1 ? originalUpper.indexOf('𝟏𝟎') : -1);
      const dayPos = originalUpper.indexOf('DAY') !== -1 ? originalUpper.indexOf('DAY') :
                    (originalUpper.indexOf('𝐃𝐚𝐲') !== -1 ? originalUpper.indexOf('𝐃𝐚𝐲') : -1);
      const ivPos = originalUpper.indexOf(' IV') !== -1 ? originalUpper.indexOf(' IV') :
                   (originalUpper.indexOf('𝐈𝐕') !== -1 ? originalUpper.indexOf('𝐈𝐕') :
                    (originalUpper.endsWith('IV') ? originalUpper.length - 2 : -1));

      if (tenKPos !== -1 && dayPos !== -1 && ivPos !== -1 &&
          tenKPos < dayPos && dayPos < ivPos + 10) { // Allow some flexibility
        if (originalUpper.includes('$') || originalUpper.includes('💸') || tenKPos < 5) {
          return true;
        }
      }
    }

    // First, normalize Unicode characters to regular ASCII (use different variable to avoid conflict)
    let normalizedFor10K = normalizeUnicodeDigits(gameName);
    normalizedFor10K = normalizeUnicodeBold(normalizedFor10K);
    normalizedFor10K = normalizedFor10K.toUpperCase();

    // Remove emojis and special characters, but keep spaces for now to check "IN A DAY"
    const cleaned = normalizedFor10K.replace(/[🔥💸]/g, '').trim();

    // Check for the pattern: must have 10K, "IN A DAY" or "INADAY", and IV
    const has10K = cleaned.includes('10K');
    const hasInADay = cleaned.includes('IN A DAY') || cleaned.includes('INADAY');
    const hasIV = cleaned.includes(' IV') || cleaned.endsWith('IV') || cleaned.includes('IV');

    if (has10K && hasInADay && hasIV) {
      // Verify order and that it's the "$10K in a Day IV" pattern
      const tenKIndex = cleaned.indexOf('10K');
      const inADayIndex = cleaned.includes('IN A DAY') ? cleaned.indexOf('IN A DAY') : cleaned.indexOf('INADAY');
      const ivIndex = cleaned.indexOf('IV');

      // Check that 10K comes before "IN A DAY"/"INADAY", which comes before IV
      if (tenKIndex !== -1 && inADayIndex !== -1 && ivIndex !== -1 &&
          tenKIndex < inADayIndex && inADayIndex < ivIndex) {
        // Additional check: should have $ sign near 10K
        const dollarNear10K = cleaned.includes('$10K') ||
                              (cleaned.includes('$') && Math.abs(cleaned.indexOf('$') - tenKIndex) < 3);
        if (dollarNear10K) {
          return true;
        }
      }
    }

    // Also try with all non-alphanumeric removed (except $) for tighter matching
    const tightNorm = cleaned.replace(/[^a-zA-Z0-9$]/g, '').toUpperCase();
    if (tightNorm.includes('$10K') && tightNorm.includes('INADAY') && tightNorm.includes('IV')) {
      const dollarIndex = tightNorm.indexOf('$10K');
      const inadayIndex = tightNorm.indexOf('INADAY');
      const ivIndex = tightNorm.indexOf('IV');

      if (dollarIndex !== -1 && inadayIndex !== -1 && ivIndex !== -1 &&
          dollarIndex < inadayIndex && inadayIndex < ivIndex) {
        return true;
      }
    }

    // Final fallback: Very permissive check - if it contains 10K, "DAY", and "IV" together
    // This catches any variant that might have been missed
    const finalCheck = normalizedFor10K.replace(/[^a-zA-Z0-9$]/g, '').toUpperCase();
    if (finalCheck.includes('10K') && finalCheck.includes('DAY') && finalCheck.includes('IV')) {
      // Make sure it's not just any game with these words - must have the structure
      const tenKPos = finalCheck.indexOf('10K');
      const dayPos = finalCheck.indexOf('DAY');
      const ivPos = finalCheck.indexOf('IV');

      // 10K should come before DAY, and DAY should come before IV (or close to it)
      if (tenKPos !== -1 && dayPos !== -1 && ivPos !== -1 &&
          tenKPos < dayPos && dayPos < ivPos + 5) { // Allow some flexibility
        // Check if there's a $ sign (should be present for "$10K")
        if (finalCheck.includes('$') || tenKPos < 3) {
          return true;
        }
      }
    }

    // No other games match
    return false;
  };

  const isTournamentGame = (gameName) => {
    if (!gameName) return false;
    if (Object.prototype.hasOwnProperty.call(tournamentOverrides, gameName)) {
      return tournamentOverrides[gameName] === true;
    }
    return matchesHardcodedTournament(gameName);
  };

  const setTournamentTag = async (gameName, nextIsTournament) => {
    if (!gameName || togglingTournamentGame) return;

    setTogglingTournamentGame(gameName);
    setTagPickerGame(null);

    setTournamentOverrides((prev) => ({
      ...prev,
      [gameName]: nextIsTournament,
    }));

    const result = await dashboardTournamentTagService.setOverride(gameName, nextIsTournament);
    setTogglingTournamentGame(null);

    if (!result.success) {
      setTournamentOverrides((prev) => {
        const next = { ...prev };
        if (Object.prototype.hasOwnProperty.call(tournamentOverrides, gameName)) {
          next[gameName] = tournamentOverrides[gameName];
        } else {
          delete next[gameName];
        }
        return next;
      });
      toast.error(result.error || 'Failed to save tournament tag');
      return;
    }

    toast.success(
      nextIsTournament
        ? 'Tournament tag added — moved below'
        : 'Tournament tag removed — moved back to cash games'
    );
  };

  const renderTournamentTagControl = (game, isTournament) => {
    const isToggling = togglingTournamentGame === game;
    const pickerOpen = tagPickerGame === game;

    return (
      <Popover
        open={pickerOpen}
        onOpenChange={(open) => setTagPickerGame(open ? game : null)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={isToggling}
            className="inline-flex items-center gap-1 max-w-full"
          >
            {isTournament ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 hover:bg-yellow-200">
                Tournament
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border border-dashed border-gray-300 text-gray-500 hover:border-yellow-400 hover:text-yellow-800">
                Add tags
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-52 p-0 bg-white border border-gray-200 shadow-lg"
          sideOffset={6}
        >
          <button
            type="button"
            disabled={isToggling}
            onClick={() => setTournamentTag(game, !isTournament)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-yellow-50 ${
              isToggling ? 'opacity-50 cursor-wait' : ''
            }`}
            title={isTournament ? 'Remove Tournament tag' : 'Add Tournament tag'}
          >
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
              Tournament
            </span>
            <span className="ml-auto text-xs text-gray-500">
              {isTournament ? 'Remove' : 'Add'}
            </span>
          </button>
        </PopoverContent>
      </Popover>
    );
  };

  // Sum the table count for a source, excluding non-tracked games (e.g. POOL
  // TABLE) and subtracting tournament tables.
  const getSourceTotals = (source) => {
    if (!source || !source.game_name) {
      return { total: 0, tournamentTotal: 0, uniqueGames: 0, peakHour: { hour: '', count: 0 } };
    }

    let totalWithTournaments = 0;
    let tournamentTotal = 0;

    source.game_name.forEach((game, index) => {
      if (isExcludedFromDashboardStats(game)) return;
      const count = source.tables[index];
      totalWithTournaments += count;
      if (isTournamentGame(game)) {
        tournamentTotal += count;
      }
    });

    const uniqueGames = [...new Set(
      source.game_name.filter((g) => !isExcludedFromDashboardStats(g))
    )].length;

    const peakHour = source.labels.reduce((peak, timestamp, index) => {
      if (isExcludedFromDashboardStats(source.game_name[index])) return peak;
      const hour = parseAnalyticsTimestamp(timestamp).format('h:mm A');
      const count = source.tables[index];
      return count > peak.count ? { hour, count } : peak;
    }, { hour: '', count: 0 });

    return {
      total: totalWithTournaments - tournamentTotal,
      tournamentTotal,
      uniqueGames,
      peakHour
    };
  };

  // Updated function to get summary statistics
  const getDataSummary = () => {
    if (!analysisData) return null;

    const mcallen = getSourceTotals(analysisData.mcallen);
    const brownsville = getSourceTotals(analysisData.brownsville);
    const brownsvilleLive = isBrownsvilleLive(analysisData);

    let totalComparison;
    if (!brownsvilleLive) {
      totalComparison = `${BROWNSVILLE_LABEL} not live yet`;
    } else if (mcallen.total > brownsville.total) {
      totalComparison = `${MCALLEN_LABEL} Leading`;
    } else if (brownsville.total > mcallen.total) {
      totalComparison = `${BROWNSVILLE_LABEL} Leading`;
    } else {
      totalComparison = 'Tied';
    }

    return {
      mcallenTotal: mcallen.total,
      brownsvilleTotal: brownsville.total,
      mcallenTournamentTotal: mcallen.tournamentTotal,
      brownsvilleTournamentTotal: brownsville.tournamentTotal,
      mcallenUniqueGames: mcallen.uniqueGames,
      brownsvilleUniqueGames: brownsville.uniqueGames,
      mcallenPeakHour: mcallen.peakHour,
      brownsvillePeakHour: brownsville.peakHour,
      brownsvilleLive,
      totalComparison
    };
  };

  const renderChart = (config) => {
    const commonProps = { options: config.options, data: config.data };

    switch (chartType) {
      case 'bar': return <Bar {...commonProps} />;
      case 'line': return <Line {...commonProps} />;
      default: return <Bar {...commonProps} />;
    }
  };

  const processAnalysisDataEnhanced = (viewType = 'time') => {
    if (!analysisData) return null;

    const mcallenSource = analysisData.mcallen || EMPTY_SOURCE;
    const brownsvilleSource = analysisData.brownsville || EMPTY_SOURCE;

    const aggregateByGameType = (source) => {
      const gameAggregation = {};
      source.labels.forEach((timestamp, index) => {
        const gameName = source.game_name[index];
        if (isTournamentGame(gameName)) return;
        if (isExcludedFromDashboardStats(gameName)) return;
        const tableCount = source.tables[index];
        gameAggregation[gameName] = (gameAggregation[gameName] || 0) + tableCount;
      });
      return gameAggregation;
    };

    // Sum tables per time slot, and average by unique day for monthly avg
    const aggregateByTime = (source) => {
      const byTimeAndDay = {};

      source.labels.forEach((timestamp, index) => {
        if (isTournamentGame(source.game_name[index])) return;
        if (isExcludedFromDashboardStats(source.game_name[index])) return;

        const momentTime = parseAnalyticsTimestamp(timestamp);
        const time = momentTime.format('h:mm A');
        const dayKey = momentTime.format('YYYY-MM-DD');
        const tableCount = source.tables[index];

        if (!byTimeAndDay[time]) byTimeAndDay[time] = {};
        byTimeAndDay[time][dayKey] = (byTimeAndDay[time][dayKey] || 0) + tableCount;
      });

      const timeAggregation = {};
      Object.entries(byTimeAndDay).forEach(([time, dayTotals]) => {
        const dailyValues = Object.values(dayTotals);
        const total = dailyValues.reduce((sum, count) => sum + count, 0);
        const dayCount = dailyValues.length;
        timeAggregation[time] = {
          total,
          avg: dayCount > 0 ? Math.round(total / dayCount) : 0,
          dayCount,
        };
      });

      return timeAggregation;
    };

    const getPeakInsights = () => {
      const weekdayTotals = {};
      const hourTotals = {};

      const accumulate = (source) => {
        source.labels.forEach((timestamp, index) => {
          if (isTournamentGame(source.game_name[index])) return;
          if (isExcludedFromDashboardStats(source.game_name[index])) return;

          const momentTime = parseAnalyticsTimestamp(timestamp);
          const weekday = momentTime.format('dddd');
          const hour = momentTime.format('h:mm A');
          const tableCount = source.tables[index];

          weekdayTotals[weekday] = (weekdayTotals[weekday] || 0) + tableCount;
          hourTotals[hour] = (hourTotals[hour] || 0) + tableCount;
        });
      };

      accumulate(mcallenSource);
      accumulate(brownsvilleSource);

      const peakDayEntry = Object.entries(weekdayTotals).sort(([, a], [, b]) => b - a)[0];
      const peakHourEntry = Object.entries(hourTotals).sort(([, a], [, b]) => b - a)[0];

      return {
        peakDay: peakDayEntry ? `${peakDayEntry[0]} (${peakDayEntry[1]} tables)` : null,
        peakHour: peakHourEntry ? `${peakHourEntry[0]} (${peakHourEntry[1]} tables)` : null,
      };
    };

    const getHourlyGameBreakdown = (source) => {
      const hourlyGames = {};
      source.labels.forEach((timestamp, index) => {
        const gameName = source.game_name[index];
        if (isTournamentGame(gameName)) return;
        if (isExcludedFromDashboardStats(gameName)) return;
        const hour = parseAnalyticsTimestamp(timestamp).format('h:mm A');
        const tableCount = source.tables[index];

        if (!hourlyGames[hour]) hourlyGames[hour] = {};
        hourlyGames[hour][gameName] = (hourlyGames[hour][gameName] || 0) + tableCount;
      });
      return hourlyGames;
    };

    const getAllGameTypes = () => {
      const mcallenGames = [...new Set(mcallenSource.game_name)];
      const brownsvilleGames = [...new Set(brownsvilleSource.game_name)];
      return [...new Set([...mcallenGames, ...brownsvilleGames])]
        .filter((g) => !isExcludedFromDashboardStats(g))
        .sort();
    };

    const getAllTimeSlots = () => {
      const mcallenTimes = mcallenSource.labels.map(timestamp =>
        parseAnalyticsTimestamp(timestamp).format('h:mm A')
      );
      const brownsvilleTimes = brownsvilleSource.labels.map(timestamp =>
        parseAnalyticsTimestamp(timestamp).format('h:mm A')
      );
      const allTimes = [...new Set([...mcallenTimes, ...brownsvilleTimes])];

      return allTimes.sort((a, b) => {
        const timeA = moment(a, 'h:mm A');
        const timeB = moment(b, 'h:mm A');

        if (timeA.hour() < 7) timeA.add(1, 'day');
        if (timeB.hour() < 7) timeB.add(1, 'day');

        return timeA.diff(timeB);
      });
    };
    switch (viewType) {
      case 'games': {
        const mcallenGameData = aggregateByGameType(mcallenSource);
        const brownsvilleGameData = aggregateByGameType(brownsvilleSource);
        const allGames = getAllGameTypes().filter(game => !isTournamentGame(game));

        return {
          labels: allGames,
          datasets: [
            {
              label: MCALLEN_LABEL,
              data: allGames.map(game => mcallenGameData[game] || 0),
              borderColor: MCALLEN_COLOR,
              backgroundColor: MCALLEN_LIGHT,
              borderWidth: 2,
            },
            {
              label: BROWNSVILLE_LABEL,
              data: allGames.map(game => brownsvilleGameData[game] || 0),
              borderColor: BROWNSVILLE_COLOR,
              backgroundColor: BROWNSVILLE_LIGHT,
              borderWidth: 2,
            }
          ]
        };
      }

      case 'hourly-games': {
        const mcallenHourlyGames = getHourlyGameBreakdown(mcallenSource);
        const brownsvilleHourlyGames = getHourlyGameBreakdown(brownsvilleSource);
        const timeSlots = getAllTimeSlots();

        // Get top 5 most active games (excluding tournament games)
        const allGameCounts = {};
        getAllGameTypes().filter(game => !isTournamentGame(game)).forEach(game => {
          const mcallenTotal = Object.values(mcallenHourlyGames).reduce((sum, hour) => sum + (hour[game] || 0), 0);
          const brownsvilleTotal = Object.values(brownsvilleHourlyGames).reduce((sum, hour) => sum + (hour[game] || 0), 0);
          allGameCounts[game] = mcallenTotal + brownsvilleTotal;
        });

        const topGames = Object.entries(allGameCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([game]) => game);

        const colors = [
          '#FF6B6B', '#4ECDC4', '#FFD166', '#6A0572', '#1A535C'
        ];

        const datasets = [];

        topGames.forEach((game, index) => {
          datasets.push({
            label: `${MCALLEN_LABEL} - ${game}`,
            data: timeSlots.map(time => mcallenHourlyGames[time]?.[game] || 0),
            borderColor: colors[index],
            backgroundColor: colors[index] + '33', // Add transparency
            borderWidth: 2,
          });

          datasets.push({
            label: `${BROWNSVILLE_LABEL} - ${game}`,
            data: timeSlots.map(time => brownsvilleHourlyGames[time]?.[game] || 0),
            borderColor: colors[index],
            backgroundColor: colors[index] + '33', // Add transparency
            borderWidth: 2,
            borderDash: [5, 5],
          });
        });

        return { labels: timeSlots, datasets };
      }

      default:
      case 'time': {
        const mcallenTimeData = aggregateByTime(mcallenSource);
        const brownsvilleTimeData = aggregateByTime(brownsvilleSource);
        const times = getAllTimeSlots();
        const peakInfo = getPeakInsights();

        return {
          labels: times,
          peakInfo,
          datasets: [
            {
              label: MCALLEN_LABEL,
              data: times.map(time => mcallenTimeData[time]?.total || 0),
              averages: times.map(time => mcallenTimeData[time]?.avg || 0),
              borderColor: MCALLEN_COLOR,
              backgroundColor: MCALLEN_LIGHT,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
            },
            {
              label: BROWNSVILLE_LABEL,
              data: times.map(time => brownsvilleTimeData[time]?.total || 0),
              averages: times.map(time => brownsvilleTimeData[time]?.avg || 0),
              borderColor: BROWNSVILLE_COLOR,
              backgroundColor: BROWNSVILLE_LIGHT,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
            }
          ]
        };
      }
    }
  };


  const dataSummary = useMemo(
    () => getDataSummary(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisData, tournamentOverrides]
  );

  const processedChartData = useMemo(
    () => (analysisData ? processAnalysisDataEnhanced(eplChartView) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analysisData, eplChartView, tournamentOverrides]
  );

  return (
    <div className="bg-gray-100 px-3 sm:px-4 py-6 sm:py-8">
      <DashboardHeader
        title="Brownsville Analytics Dashboard"
        dateLabel={formatDateRangeDisplay()}
        onOpenDatePicker={() => setIsDatePickerOpen(true)}
        chartType={chartType}
        onChartTypeChange={setChartType}
        onRefresh={() => loadDashboardAnalytics(true)}
        refreshing={analysisLoading}
      />

      {isDatePickerOpen && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
          initialSelectedRange="thisMonth"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          <div className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-sm lg:col-span-2">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-3 mb-4">
              <h2 className="text-lg sm:text-xl font-semibold">{MCALLEN_LABEL} vs {BROWNSVILLE_LABEL} Detailed Analysis</h2>
              <div className="w-full xl:w-auto flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex items-center justify-between sm:justify-start">
                  <label className="mr-2 font-medium text-sm">View:</label>
                  <select
                    value={eplChartView}
                    onChange={(e) => setEplChartView(e.target.value)}
                    className="border rounded p-1 text-sm w-[170px] sm:w-auto"
                  >
                    <option value="time">By Time</option>
                    <option value="games">By Game Type</option>
                    <option value="hourly-games">Top Games by Hour</option>
                  </select>
                </div>
                <span className="text-xs sm:text-sm text-blue-600">
                  {formatAllLocationsHoursLabel()}
                </span>
              </div>
            </div>

            {!analysisLoading && !analysisError && analysisData && (
              <div className="mb-6">
                <div className="mb-2 text-xs text-gray-500 italic">
                  * Tournament tables excluded from totals (shown separately in table below)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                  {dataSummary ? (
                    <>
                      <DashboardStatCard
                        label={`${MCALLEN_LABEL} Total Tables`}
                        value={dataSummary.mcallenTotal}
                        hint={dataSummary.mcallenTournamentTotal > 0 ? `(Excl. ${dataSummary.mcallenTournamentTotal} tournament)` : null}
                        tone="blue"
                      />
                      <DashboardStatCard
                        label={`${BROWNSVILLE_LABEL} Total Tables`}
                        value={dataSummary.brownsvilleLive ? dataSummary.brownsvilleTotal : 'Not live yet'}
                        hint={
                          dataSummary.brownsvilleLive && dataSummary.brownsvilleTournamentTotal > 0
                            ? `(Excl. ${dataSummary.brownsvilleTournamentTotal} tournament)`
                            : null
                        }
                        tone="amber"
                      />
                      <DashboardStatCard
                        label="Comparison Status"
                        value={dataSummary.totalComparison}
                        tone="green"
                      />
                      <DashboardStatCard
                        label="Unique Games"
                        value={`${MCALLEN_LABEL}: ${dataSummary.mcallenUniqueGames} | ${BROWNSVILLE_LABEL}: ${dataSummary.brownsvilleUniqueGames}`}
                        tone="purple"
                      />
                    </>
                  ) : null}
                </div>
              </div>
            )}

            <div className="h-[280px] sm:h-[360px] lg:h-96">
              {analysisLoading && !analysisData ? (
                <div className="flex h-full items-center justify-center">
                  <DashboardLoader />
                </div>
              ) : analysisError ? (
                <div className="text-center py-10">
                  <div className="text-red-500 mb-2">Failed to load analytics data</div>
                  <div className="text-gray-600 text-sm">{analysisError}</div>
                  <button
                    onClick={() => loadDashboardAnalytics(true)}
                    className="mt-4 rounded-xl bg-blue-600 px-4 py-2 text-white shadow-sm hover:bg-blue-500"
                  >
                    Retry
                  </button>
                </div>
              ) : analysisData ? (
                (() => {
                  const chartTitle = {
                    'time': `Table Count by Time - ${moment(dateRange.start).tz(APP_TIMEZONE).format('MMM D, YYYY')} to ${moment(dateRange.end).tz(APP_TIMEZONE).format('MMM D, YYYY')}`,
                    'games': `Table Count by Game Type - ${moment(dateRange.start).tz(APP_TIMEZONE).format('MMM D, YYYY')} to ${moment(dateRange.end).tz(APP_TIMEZONE).format('MMM D, YYYY')}`,
                    'hourly-games': `Top 5 Games Hourly Breakdown - ${moment(dateRange.start).tz(APP_TIMEZONE).format('MMM D, YYYY')} to ${moment(dateRange.end).tz(APP_TIMEZONE).format('MMM D, YYYY')}`,
                  }[eplChartView];

                  if (!processedChartData) {
                    return (
                      <div className="text-center py-10 text-gray-500">
                        No data available for the selected view and time range
                      </div>
                    );
                  }

                  return renderChart({
                    options: getEnhancedChartOptions(
                      chartTitle,
                      eplChartView,
                      eplChartView === 'time' ? processedChartData.peakInfo : null
                    ),
                    data: processedChartData
                  });
                })()
              ) : (
                <div className="text-center py-10 text-gray-500">
                  No analytics data available
                </div>
              )}
            </div>

            {eplChartView === 'time' && processedChartData?.peakInfo && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-indigo-50 border border-indigo-100 rounded p-3">
                  <div className="text-xs text-indigo-600 font-medium">Peak Day of Week</div>
                  <div className="text-sm font-bold text-indigo-800">
                    {processedChartData.peakInfo.peakDay || 'N/A'}
                  </div>
                </div>
                <div className="bg-rose-50 border border-rose-100 rounded p-3">
                  <div className="text-xs text-rose-600 font-medium">Peak Hour of Day</div>
                  <div className="text-sm font-bold text-rose-800">
                    {processedChartData.peakInfo.peakHour || 'N/A'}
                  </div>
                </div>
              </div>
            )}

            {/* Game Details Table (when viewing by games) */}
            {!analysisLoading && !analysisError && analysisData && eplChartView === 'games' && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Game Type Details</h3>
                <p className="text-sm text-gray-500 mb-3">
                  Click <span className="font-medium text-gray-600">Add tags</span>, then click{' '}
                  <span className="font-medium text-yellow-800">Tournament</span> to move a game below.
                  Open the tag in Tournaments and click Remove to move it back.
                </p>
                <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="min-w-[640px] sm:min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Game Name
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tags
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {MCALLEN_LABEL} Tables
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {BROWNSVILLE_LABEL} Tables
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Difference
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(() => {
                        const mcallenGames = {};
                        const brownsvilleGames = {};

                        const mcallenSource = analysisData.mcallen || EMPTY_SOURCE;
                        const brownsvilleSource = analysisData.brownsville || EMPTY_SOURCE;

                        mcallenSource.game_name.forEach((game, index) => {
                          mcallenGames[game] = (mcallenGames[game] || 0) + mcallenSource.tables[index];
                        });

                        brownsvilleSource.game_name.forEach((game, index) => {
                          brownsvilleGames[game] = (brownsvilleGames[game] || 0) + brownsvilleSource.tables[index];
                        });

                        const allGames = [...new Set([...Object.keys(mcallenGames), ...Object.keys(brownsvilleGames)])]
                          .filter((g) => !isExcludedFromDashboardStats(g))
                          .filter((game) => (mcallenGames[game] || 0) > 0 || (brownsvilleGames[game] || 0) > 0);

                        const regularGames = allGames.filter((game) => !isTournamentGame(game)).sort();
                        const tournamentGames = allGames.filter((game) => isTournamentGame(game)).sort();

                        const renderGameRow = (game, index, isTournament) => {
                          const mcallenCount = mcallenGames[game] || 0;
                          const brownsvilleCount = brownsvilleGames[game] || 0;
                          const difference = mcallenCount - brownsvilleCount;

                          return (
                            <tr
                              key={game}
                              className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isTournament ? 'bg-yellow-50/60 border-l-4 border-yellow-400' : ''}`}
                            >
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {game}
                              </td>
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                                {renderTournamentTagControl(game, isTournament)}
                              </td>
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-semibold">
                                {mcallenCount}
                              </td>
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-amber-600 font-semibold">
                                {brownsvilleCount}
                              </td>
                              <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-semibold ${difference > 0 ? 'text-green-600' : difference < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                {difference > 0 ? '+' : ''}{difference}
                              </td>
                            </tr>
                          );
                        };

                        return (
                          <>
                            <tr className="bg-slate-100">
                              <td colSpan={5} className="px-3 sm:px-6 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
                                Cash games ({regularGames.length})
                              </td>
                            </tr>
                            {regularGames.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-3 sm:px-6 py-4 text-sm text-gray-400">
                                  No cash games in this range
                                </td>
                              </tr>
                            ) : (
                              regularGames.map((game, index) => renderGameRow(game, index, false))
                            )}

                            <tr className="bg-yellow-100">
                              <td colSpan={5} className="px-3 sm:px-6 py-2 text-xs font-semibold uppercase tracking-wide text-yellow-900">
                                Tournaments ({tournamentGames.length})
                              </td>
                            </tr>
                            {tournamentGames.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-3 sm:px-6 py-4 text-sm text-gray-400">
                                  No tournament games yet — add the Tournament tag above to move one here
                                </td>
                              </tr>
                            ) : (
                              tournamentGames.map((game, index) => renderGameRow(game, index, true))
                            )}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

        </div>
      {/* Day-wise Breakdown for Multi-day Ranges */}
      {isMultipleDaysRange() && showDayWiseSection && (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-sm lg:col-span-2 mt-8">
          <h2 className="mb-4 text-lg sm:text-xl font-semibold">Day-wise Breakdown</h2>

          <DayWiseBreakdown
            entries={sortedDayWiseEntries}
            loading={dayWiseLoading}
            chartType={chartType}
            parseTimestamp={parseAnalyticsTimestamp}
            isTournamentGame={isTournamentGame}
            isExcludedFromDashboardStats={isExcludedFromDashboardStats}
          />
        </div>
      )}

      <ResetTokesButton />
    </div>
  );
};

export default Dashboard;
